/* import __COLOCATED_TEMPLATE__ from './help-center-audience-filter.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { audienceRoles } from 'embercom/models/data/matching-system/matching-constants';
import { audienceRoleLabel } from 'embercom/helpers/audience-types';

interface Args {
  doSearch: (audienceTypes?: string[]) => void;
  selectedValues?: string[];
  activeFilter?: string;
  removeFilter: () => void;
}

interface Signature {
  Args: Args;
}

interface SelectOption {
  text: string;
  value: string;
  isSelected: boolean;
}

export default class HelpCenterAudienceFilter extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked selectedValues: string[] = this.args.selectedValues || [];
  lastSyncedPropsString = JSON.stringify([...(this.args.selectedValues || [])].sort());

  @action
  syncWithProps() {
    let propsValues = this.args.selectedValues || [];
    let propsString = JSON.stringify([...propsValues].sort());

    if (this.lastSyncedPropsString === propsString) {
      return;
    }

    let currentString = JSON.stringify([...this.selectedValues].sort());
    if (currentString !== propsString) {
      this.selectedValues = propsValues;
    }

    this.lastSyncedPropsString = propsString;
  }

  get isOpen() {
    return (
      this.args.activeFilter === 'helpCenterAudienceRoles' && !this.args.selectedValues?.length
    );
  }

  get items(): SelectOption[] {
    let allSelected = this.selectedValues.length === 0;
    return [
      {
        text: audienceRoleLabel(audienceRoles.users),
        value: audienceRoles.users,
        isSelected: allSelected || this.selectedValues.includes(audienceRoles.users),
      },
      {
        text: audienceRoleLabel(audienceRoles.visitors),
        value: audienceRoles.visitors,
        isSelected: allSelected || this.selectedValues.includes(audienceRoles.visitors),
      },
      {
        text: audienceRoleLabel(audienceRoles.leads),
        value: audienceRoles.leads,
        isSelected: allSelected || this.selectedValues.includes(audienceRoles.leads),
      },
    ];
  }

  get details() {
    if (this.selectedValues.length === 0) {
      return this.intl.t('knowledge-hub.filterable-list.filters.help-center-audience.is-roles', {
        roles: this.intl.t('knowledge-hub.filterable-list.filters.help-center-audience.everyone'),
      });
    }

    if (this.selectedValues.length === this.items.length) {
      return this.intl.t('knowledge-hub.filterable-list.filters.help-center-audience.is-roles', {
        roles: this.intl.t('knowledge-hub.filterable-list.filters.help-center-audience.everyone'),
      });
    }

    let selectedItems = this.items.filter((item) => this.selectedValues.includes(item.value));
    let separator = this.intl.t(
      'knowledge-hub.filterable-list.filters.help-center-audience.or-separator',
    );
    let selectedText = selectedItems.map((item) => item.text).join(separator);
    return this.intl.t('knowledge-hub.filterable-list.filters.help-center-audience.is-roles', {
      roles: selectedText,
    });
  }

  @action removeFilter() {
    this.selectedValues = [];
    this.lastSyncedPropsString = '[]';
    this.args.doSearch(undefined);
    this.args.removeFilter();
  }

  @action onSelectionChange(selectedValues: string[]) {
    if (selectedValues.length === 0) {
      this.removeFilter();
      return;
    }
    this.selectedValues = selectedValues;
    this.lastSyncedPropsString = JSON.stringify([...selectedValues].sort());
    this.args.doSearch(selectedValues);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::Filters::HelpCenterAudienceFilter': typeof HelpCenterAudienceFilter;
    'knowledge-hub/filterable-list/filters/help-center-audience-filter': typeof HelpCenterAudienceFilter;
  }
}
